/**
 * scrollFade — Svelte action for a horizontal scroller (tab / chip rows).
 *
 * - Sets `data-fade-start` / `data-fade-end` on the node while there is more
 *   content to scroll to on that side, so CSS can fade the edge (see the
 *   `mask-image` rule in Projects/Index.svelte).
 * - Scrolls the active item into view on mount and whenever `active` changes.
 *   It moves the scroller's own `scrollLeft` (not `scrollIntoView`), so the
 *   page never jumps vertically.
 *
 * `enabled: false` removes everything, which keeps desktop and tablets unchanged.
 */
export interface ScrollFadeParams {
  enabled: boolean;
  /** Any value that changes when the active item changes; triggers a re-reveal. */
  active?: unknown;
  /** Selector of the active item inside the node. */
  activeSelector?: string;
}

const EDGE_TOLERANCE = 2;

export function updateFade(node: HTMLElement) {
  const max = node.scrollWidth - node.clientWidth;
  if (max <= EDGE_TOLERANCE) {
    delete node.dataset.fadeStart;
    delete node.dataset.fadeEnd;
    return;
  }
  const left = node.scrollLeft;
  if (left > EDGE_TOLERANCE) node.dataset.fadeStart = 'true';
  else delete node.dataset.fadeStart;
  if (left < max - EDGE_TOLERANCE) node.dataset.fadeEnd = 'true';
  else delete node.dataset.fadeEnd;
}

export function revealActive(node: HTMLElement, selector: string) {
  updateFade(node);
  const item = node.querySelector<HTMLElement>(selector);
  if (!item) return;
  const nodeRect = node.getBoundingClientRect();
  const itemRect = item.getBoundingClientRect();
  // Centre the item when it is clipped on either side; leave it alone otherwise.
  const margin = 24;
  if (itemRect.left < nodeRect.left + margin || itemRect.right > nodeRect.right - margin) {
    const target =
      node.scrollLeft + (itemRect.left - nodeRect.left) - (nodeRect.width - itemRect.width) / 2;
    node.scrollLeft = Math.max(0, target);
    updateFade(node);
  }
}

export function scrollFade(node: HTMLElement, params: ScrollFadeParams) {
  let current = params;
  let observer: ResizeObserver | null = null;
  let attached = false;

  const onScroll = () => updateFade(node);

  function attach() {
    if (attached) return;
    attached = true;
    node.addEventListener('scroll', onScroll, { passive: true });
    if (typeof ResizeObserver !== 'undefined') {
      // The node's own size and its children's: a tab growing (count, font load)
      // changes scrollWidth without resizing the scroller.
      observer = new ResizeObserver(onScroll);
      observeChildren();
    }
  }

  // Also called after an active change, since the view may have swapped its children.
  function observeChildren() {
    if (!observer) return;
    observer.disconnect();
    observer.observe(node);
    for (const child of node.children) observer.observe(child);
  }

  function detach() {
    if (!attached) return;
    attached = false;
    node.removeEventListener('scroll', onScroll);
    observer?.disconnect();
    observer = null;
    delete node.dataset.fadeStart;
    delete node.dataset.fadeEnd;
  }

  function sync(reveal: boolean) {
    if (!current.enabled) {
      detach();
      return;
    }
    attach();
    if (!reveal) return;
    // Wait for the DOM update that changed the active item to land.
    queueMicrotask(() => {
      if (!current.enabled) return;
      observeChildren();
      revealActive(node, current.activeSelector ?? '.active');
    });
  }

  sync(true);

  return {
    update(next: ScrollFadeParams) {
      // Svelte re-runs update() whenever any reactive value in the param is
      // re-evaluated (e.g. every viewport store emit on resize). Only re-reveal
      // when the active item or the enabled flag changed, so a row the user has
      // scrolled isn't snapped back.
      const changed = next.enabled !== current.enabled || next.active !== current.active;
      current = next;
      sync(changed);
    },
    destroy() {
      detach();
    },
  };
}
